import Banner from "~/components/banner"
import CurveDetectorBanner from "./banner"
import RenderCofabComponent from "~/renderCofabComponent"
import Figure from "~/components/figure"
import Tuning1 from "./tuning1"
import DatasetFigure from "./datasetFigure"
import WhatIsACurve from "./whatIsACurve"
import Synthetic1 from "./synthetic1"
import FeatureVisualization from "./featureVisualization"
import SyntheticSingle from "./syntheticSingle"
import FeatureVisNegative from "./featureVisNegative"
import RadialInceptionV1 from "./radialInceptionV1"
import RadialAcrossModels from "./radialAcrossModels"
import FirstRadial from "./firstRadial"

const SectionLabel = ({ background = "rgba(0, 0, 0, 0.04)", ...props }) => (
  <Surface
    paddingX={10}
    fontSize={20}
    paddingY={4}
    border
    alignItems="center"
    justifyContent="center"
    fontWeight={700}
    borderRadius={12}
    textTransform="uppercase"
    color="white"
    background={background}
    {...props}
  />
)

const SectionTitle = ({ index, title }) => (
  <Surface gridColumn="text">
    <Surface
      borderBottom
      marginTop={32}
      marginBottom={24}
      paddingBottom={16}
      width="100%"
      justifyContent="space-between"
      flexFlow="row"
      alignItems="center"
    >
      <Text color="black" fontSize={30} lineHeight="20px" fontWeight={600}>
        {title}
      </Text>
      <SectionLabel color="rgba(0, 0, 0, 0.6)">Argument {index}</SectionLabel>
    </Surface>
  </Surface>
)

export default class CurveDetector extends Component {
  render() {
    return (
      <React.Fragment>
        <Banner
          title="Curve Detectors"
          subtitle={
            <span style={{ lineHeight: 1.2 }}>
              A detailed look at a nearly universally occuring family of units
              that form an important part of early vision networks.
            </span>
          }
          figure={<CurveDetectorBanner />}
        />
        <Paragraph display="inline">
          Curve detectors are an easily recognizable family of units that are
          almost universally present in convolutional networks trained on
          natural images, typically accounting for 2-5% of early vision. They’ve
          been hinted at in the literature as far back as 2013 (see figures in{" "}
          <a href="https://arxiv.org/abs/1311.2901" target="_blank">
            Zeiler &amp; Fergus
          </a>
          ), but don’t seem to have been explicitly discussed or characterized.
        </Paragraph>
        <Paragraph>
          We believe curve detectors are worth detailed analysis for three
          reasons:
        </Paragraph>
        <Paragraph paddingLeft={40}>
          <b>A Universal Feature</b>? Like Gabor filters, curve detectors seem
          to be a reliably recurring feature across a wide variety of
          convolutional neural networks trained on multiple natural image
          datasets. They also seem analogous to neurons found in V4 of macaque
          [] and rhesus monkeys [].
        </Paragraph>
        <Paragraph paddingLeft={40}>
          <b>A Test Case for Meaningful Features:</b> The machine learning
          community presently disagrees on whether hidden layers contain
          “meaningful features” in some sense. Curve detectors seem like an
          excellent test case for sharpening this dialogue. They’re a relatively
          modest step from Gabor filters which the community does universally
          accept, and their simplicity makes many experiments one might wish to
          run easy. We hope this article will either slightly advance the
          conversation by bringing agreement that curve detectors exist, or help
          isolate a deeper crux underlying the disagreement.
        </Paragraph>
        <Paragraph paddingLeft={40}>
          <b>Motif Demonstration:</b> The circuits implementing curve detectors
          exhibits several “circuit motifs” we see recurring throughout circuits
          across models. In particular, curve detectors are an equivariant
          family of neurons, and this equivariance is reflected in the circuit.
          They also demonstrate the role of negative weights in increasing
          specialization, and the mosaic pattern of overlapping features
          creating larger structures.
        </Paragraph>
        <Paragraph>
          This article offers a detailed characterization of curve detectors and
          shows that they exist across a wide variety of models. We show not
          only that curve detectors empirically fire in response to curves, and
          also that the reaction is causal. In a follow up article, we will
          characterize the circuits involved in curve detection: how curve
          detectors are assembled from earlier features, and how they’re used by
          downstream client neurons.
        </Paragraph>
        <Heading>What is a Curve Detector</Heading>
        <Paragraph>
          A curve detector is a feature that responds to roughly between eighth
          and quarter arcs of a circle. Although we have found that curve
          detectors exist in many models, we will focus on the curve detectors
          in InceptionV1, which are found in the 5x5 branch of layer 3b.
        </Paragraph>
        <WhatIsACurve />
        <Paragraph display="inline">
          Are the curve detectors <i>really</i> curve detectors? Since there has
          been disagreement about this kind of claim in the past, we offer six
          different arguments. Taken collectively, we believe the evidence is
          quite strong.
        </Paragraph>
        <Paragraph>
          The first four arguments are based on the properties of the features
          in isolation:
        </Paragraph>
        <ol>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Feature Visualization: If we optimize the input to create an image
            that maximally activates these units we produce curves, as seen
            above. Note that this is without a generative model prior, so
            anything in the resulting image must have caused the neuron to fire
            more. Running the optimization process from different random seeds
            reliably produces curves.
          </li>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Dataset examples: If we look at places where ImageNet images cause
            these neurons to strongly fire, they are reliably curves in the
            expected orientation. If we look at examples where the neurons fired
            moderately, they are generally less perfect curves or curves off
            orientation.
          </li>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Tuning Curves: If we take dataset examples that cause a neuron to
            fire and rotate them, they gradually stop firing and the curve
            detectors in the next orientation begins firing. This shows that
            they detect they are detecting rotated versions of the same thing.
          </li>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Synthetic Examples: Based on our theory that these are curve
            detectors, we can synthesize artificial curves in varying
            orientations, curvatures, and backgrounds. The curve detectors fire
            reliably for curves in the correct orientation.
          </li>
        </ol>
        <Paragraph>
          The last two arguments rely on circuits, which we’ll discuss in a
          second article. But in brief, they will be:
        </Paragraph>
        <ol start={5}>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Feature implementation: By looking at the circuit constructing the
            curve detectors, we can literally read a curve detection algorithm
            off of the weights. We also don’t see anything obviously suggestive
            of a second case, although there are many smaller weights we don’t
            understand the role of.
          </li>
          <li
            style={{
              fontSize: 17,
              lineHeight: 1.5,
              marginY: 10,
              color: "black",
              fontWeight: 400,
            }}
          >
            Feature usage: The downstream clients of curve detectors are
            features that naturally involve curves (eg. circles, 3d curvature,
            spirals…). The curve detectors are used by these clients in the way
            our theory of the two features would predict.
          </li>
        </ol>
        <SectionTitle title="Feature Visualization" index={1} />
        <Paragraph>
          Feature visualization creates an example of an image that makes a
          neuron activate strongly. It starts with an image of random noise and
          optimizes it to make the neuron fire maximally.
        </Paragraph>
        <Paragraph>
          Since each image is created from scratch, we can be confident that
          anything in the image causes the neuron to fire. To see different
          cases where the neuron fires, we can run multiple feature
          visualizations in parallel with a diversity term that incentivizes
          them to be different.
        </Paragraph>
        <RenderCofabComponent
          folder="curve-diversity"
          component={FeatureVisualization}
        />
        <Paragraph>
          We can also use feature visualization to see what most negatively
          activates a neuron.
        </Paragraph>
        <RenderCofabComponent
          folder="curves-negative-feature-vis"
          component={FeatureVisNegative}
        />
        <SectionTitle title="Dataset Examples" index={2} />
        <Paragraph>
          Feature visualization is a powerful technique but it doesn't tell us
          what neurons respond to in practice, or what neurons respond to points
          between the extremes of positive and negative.
        </Paragraph>
        <Paragraph>
          To understand more, we can find examples in ImageNet that make curve
          neurons activate.
        </Paragraph>
        <Paragraph>
          Dataset examples are especially helpful for seeing the full diversity
          of inputs that cause a neuron to fire. One worry you might have after
          our feature visualization argument is that curves might be one of
          several things that cause the neuron to fire, and feature
          visualization is only discovering one of them. But careful examination
          of dataset examples can increase our confidence this isn’t the case.
        </Paragraph>
        <RenderCofabComponent
          folder="curve-dataset-examples"
          component={DatasetFigure}
        />
        <SectionTitle title="Tuning Curves" index={3} />
        <Paragraph>
          Our first two experiments suggest that each curve detector responds to
          curves at a different orientation. Our next experiment will help
          verify that they really do detect rotated versions of the same
          feature, and characterize how sensitive each unit is to changes in
          orientation.
        </Paragraph>
        <Paragraph>
          We do this by creating a <i>joint tuning curve</i> of how all curve
          detectors respond if we rotate natural dataset examples that caused a
          particular curve detector to fire. We observe that each curve detector
          has a gaussian-like bump around it’s preferred orientation, and as
          each one stops firing another begins to fire, taking its place.
          Jointly, they span all orientations of curves.
        </Paragraph>
        <Tuning1 />
        <SectionTitle title="Synthetic Examples" index={4} />
        <Paragraph>
          One sign of understanding something is that you can make it from
          scratch. In this spirit, our final experiment will be synthetically
          generating curves to prod our curve detector with. Some features are
          too complicated for us to easily make synthetic examples, but for
          simple features like curves it’s easy to do.
        </Paragraph>
        <Paragraph>
          One of the nice things about synthetically generating curves is that
          we can explicitly test how the detectors respond to different
          variations in curves. Do our curve detectors respond to a range of
          curvatures? What orientations do they respond to? Do they respond to
          curves which are both a line on a solid background and a boundary
          between foreground and background? Does it matter what colors are
          involved? Creating our own synthetic examples allows us to test all
          these questions.
        </Paragraph>
        <Paragraph>
          As we saw in the previous section, our curve detectors are selective
          for a certain range of orientations. We also see that while they
          respond to a wide range of curvatures, there is a radius they prefer.
          And they seem to be very robust to what kind of line or boundary they
          respond to!
        </Paragraph>
        <RenderCofabComponent folder="synthetic-3b" component={Synthetic1} />
        <Paragraph>
          Since the curves are invariant to fill and background, we can zoom in
          on a single quadrant to see more detail.
        </Paragraph>
        <SyntheticSingle />
        <Paragraph>
          To see how multiple neurons respond to synthetic curves at a glance,
          we can average the height dimension of the responses and map it onto a
          circle, visualizing the angle of peak response with color.
        </Paragraph>
        <Surface marginY={30}>
          <RenderCofabComponent
            component={FirstRadial}
            folder="radial-tuning-curve"
          />
        </Surface>
        <Paragraph>
          We can also visualize curve detectors from several layers side by side
          using this interface. Because angles are mapped to color, their hues
          are comparable.
        </Paragraph>
        <Figure>
          <Surface>
            <FigureHeading>
              Responses of Curve Neurons across InceptionV1
            </FigureHeading>
            <Surface marginY={30}>
              <RenderCofabComponent
                component={RadialInceptionV1}
                folder="radial-tuning-curve"
              />
            </Surface>
          </Surface>
        </Figure>
        <Paragraph>We can also compare models.</Paragraph>
        <Figure>
          <Surface>
            <FigureHeading>
              Responses of Curve Neurons across Models
            </FigureHeading>
            <Surface marginY={30}>
              <RenderCofabComponent
                component={RadialAcrossModels}
                folder="radial-curves-across-models"
              />
            </Surface>
          </Surface>
        </Figure>
        <Heading>Conclusion</Heading>
        <Paragraph>
          We believe that curve detectors are a good example for debating the
          existence of semantically meaningful features in neural network. We
          are presenting six arguments that we believe are collectively strong
          evidence that there exist curve detectors in InceptionV1.
        </Paragraph>
        <Paragraph>
          In this article we took a black-box approach, studying these neurons
          as a neuroscientist might, using techniques like tuning curves and
          synthetic generators.
        </Paragraph>
        <Paragraph>
          In the following article, we’ll zoom in and look at the circuits
          surrounding curve detectors. By looking at individual learned
          parameters, we’ll mechanistically explain some of the behavior we’ve
          experimentally shown above.
        </Paragraph>
        <Surface marginY={60} />
      </React.Fragment>
    )
  }
}

/*
        <Surface
          width={600}
          gridColumn="text"
          paddingX={40}
          paddingY={10}
          background="hsl(54, 78%, 96%)"
          marginY={20}
          style={{ borderLeft: "solid hsl(54, 33%, 67%) 1px" }}
        >
          <Surface
            flexFlow="row"
            alignItems="center"
            justifyContent="space-between"
            paddingBottom={10}
            borderBottom
          >
            <Text marginTop={10} size={600}>
              Group Convolution
            </Text>
            <Surface
              alignItems="center"
              flexFlow="row"
              width={160}
              paddingY={5}
              paddingX={10}
              borderRadius={5}
              border
              cursor="pointer"
              onClick={() => (window.location = "/motifs/groupConv")}
            >
              <div
                style={{
                  content: `url(https://distill.pub/2019/advex-bugs-discussion/images/single-page.svg)`,
                  width: 20,
                }}
              />
              <Text marginX={8} size={500} fontWeight={600}>
                Read Paper
              </Text>
              <Icon opacity={0.8} icon="arrow-right" />
            </Surface>
          </Surface>
          <Paragraph>
            We believe curves are approximating a motif called a group
            convolution, which we covered in another thread.
            <br />
            <br />
            We believe it is an important part of the story of curves, and we
            recommend reading it if you wish to learn more.
          </Paragraph>
        </Surface>

        <Paragraph display="inline">
          We were surprised to find that the model learned group convolution
          being learned by the model without it being baked into the
          architecture. There is a long history of models finding{" "}
          <a href="https://arxiv.org/pdf/1803.03453.pdf">creative solutions</a>{" "}
          that researchers didn’t intend, but the analogous situation has not
          been demonstrated in the representations.
        </Paragraph>
        <Paragraph>
          Many of the biggest advances in machine learning — including
          convolutional neural networks — have been about imbuing models with
          useful priors. We believe this points towards a promising research
          direction of studying the representations of models with high degrees
          of freedom to discover positive ways to reduce this freedom.
        </Paragraph>

        <Heading>Understanding Weights</Heading>
        <Paragraph>
          To get a deeper understanding of curve detectors, we can look at the
          individual learned parameters that the model uses to construct the
          units.
          <br />
          <br />
          Specifically we'll be looking at the curves of layer mixed3b of the
          InceptionV1 model. Although curves are refined over several layers
          &mdash; the mosaic motif &mdash; the general story of their weights is
          the same: compose edges and less refined curves along the angle of
          interest in the weights, forming a curve detector.
        </Paragraph>
        <Mosaic />
        <Paragraph>
          Interestingly, negative weights are used to increase the specificity
          of units. They seem to primarily be used to differentiate between
          units that are “close enough” that there might otherwise be confusion.
        </Paragraph>
        <RenderCofabComponent
          folder="curves-negative-weights"
          component={NegativeWeights}
        />
*/
